import { IAppContext } from 'App/AppContext';
import { TFunction } from 'i18next';
import { apiGetResourceListName } from 'services/Api/listService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';

export enum ResourceListType {
  DocumentLibrary = 0,
  CustomList = 1,
  WebURL = 2,
  SitePageLibrary = 3,
}

export enum ResourceListSystemType {
  EMailAttachments = 1, // For the Outlook task app to upload attachments
  Evidence = 2, // not used, but reserved for future use to upload evidence from a task with 1-button-click
  Assets = 3, // To link a list of sub(assets) to an asset like computers, mobile devices etc.
  Threats = 4, // List to link to a risk to select threats
  BaseEvidence = 5, // Base evidence list to link a KPI
}

export default class ResourceList {
  tenantId?: string;

  listId: number;

  authSchemaId?: number;

  listType: ResourceListType;

  systemListId?: ResourceListSystemType;

  spSiteId?: string;

  spListId?: string;

  spDriveId?: string;

  spDriveItemId?: string;

  name: string;

  webURL?: string;

  altTenantId?: string;

  altTenantName?: string;

  columnProps?: string;

  enableMetaData?: boolean;

  isVirtual?: boolean;

  skipPreview?: boolean;

  constructor() {
    this.listId = -1;
    this.listType = ResourceListType.DocumentLibrary;
    this.spSiteId = '';
    this.spListId = '';
    this.spDriveId = '';
    this.spDriveItemId = '';
    this.name = '';
    this.webURL = '';
  }

  canEnableMetaData = (): boolean => {
    return this.listType === ResourceListType.DocumentLibrary || this.listType === ResourceListType.SitePageLibrary;
  };

  isEqual(item: ResourceList): boolean {
    if (item.listId !== this.listId) return false;
    if (item.listType !== this.listType) return false;
    if (item.spSiteId !== this.spSiteId) return false;
    if (item.spDriveId !== this.spDriveId) return false;
    if (item.spListId !== this.spListId) return false;
    if (item.spDriveItemId !== this.spDriveItemId) return false;
    if (item.name !== this.name) return false;
    if (item.webURL !== this.webURL) return false;
    if (item.altTenantId !== this.altTenantId) return false;
    if (item.altTenantName !== this.altTenantName) return false;
    if (item.columnProps !== this.columnProps) return false;
    if (item.enableMetaData !== this.enableMetaData) return false;
    if (item.isVirtual !== this.isVirtual) return false;
    if (item.authSchemaId !== this.authSchemaId) return false;
    if (item.skipPreview !== this.skipPreview) return false;

    return true;
  }

  clone = (): ResourceList => {
    const output = new ResourceList();

    output.listId = this.listId;
    output.listType = this.listType;
    output.spSiteId = this.spSiteId;
    output.spDriveId = this.spDriveId;
    output.spListId = this.spListId;
    output.spDriveItemId = this.spDriveItemId;
    output.name = this.name;
    output.webURL = this.webURL;
    output.altTenantId = this.altTenantId;
    output.altTenantName = this.altTenantName;
    output.columnProps = this.columnProps;
    output.enableMetaData = this.enableMetaData;
    output.isVirtual = this.isVirtual;
    output.authSchemaId = this.authSchemaId;
    output.skipPreview = this.skipPreview;

    return output;
  };

  static async getUniqueName(orgName: string, appContext: IAppContext): Promise<string> {
    let suffix: number = 1;

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    let listExists: number = await apiGetResourceListName(orgName, accessToken);

    let newName: string = orgName;

    while (listExists) {
      const name = newName + ' ' + suffix.toString();
      listExists = await apiGetResourceListName(name, accessToken);
      if (suffix > 100) throw new AppError('Create new list name failed: 100 iterations reached');
      suffix++;
      newName = name;
    }

    return newName;
  }

  static async nameExists(orgName: string, appContext: IAppContext): Promise<number> {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);

    return await apiGetResourceListName(orgName, accessToken);
  }

  getListTypeText(t: TFunction, type?: ResourceListType): string {
    switch (type ?? this.listType) {
      case ResourceListType.DocumentLibrary:
        return t('lists:List.Type.DocumentLibrary');
      case ResourceListType.CustomList:
        return t('lists:List.Type.CustomList');
      case ResourceListType.WebURL:
        return t('lists:List.Type.URLList');
      case ResourceListType.SitePageLibrary:
        return t('lists:List.Type.SitePageLibrary');
      default:
        return '';
    }
  }
}
